import _isEmpty from 'lodash/isEmpty';

/**
 * Replaces template placeholders in a string with corresponding values
 *
 * @example
 * // Basic usage
 * replaceTemplatePlaceholders('Hello #name!', { name: 'World' })
 * // Returns: 'Hello World!'
 *
 * // Multiple formats
 * replaceTemplatePlaceholders('Hello #name and <type>!', { name: 'World', type: 'Universe' })
 * // Returns: 'Hello World and Universe!'
 *
 * // Case insensitive
 * replaceTemplatePlaceholders('Hello #NAME!', { name: 'World' }, { caseSensitive: false })
 * // Returns: 'Hello World!'
 *
 * @param {string} template - The template string containing placeholders
 * @param {Object} replacements - Object containing key-value pairs for replacements
 * @param {Object} options - Configuration options
 * @param {boolean} options.caseSensitive - Whether matching should be case sensitive (default: true)
 * @param {string[]} options.formats - Array of placeholder formats to support (default: ['#', '<>', '{}'])
 * @returns {string} - The template with placeholders replaced
 */
export const replaceTemplatePlaceholders = (
  template,
  replacements = {},
  options = {},
) => {
  // Input validation
  if (!template || typeof template !== 'string') {
    return template;
  }

  if (_isEmpty(replacements) || typeof replacements !== 'object') {
    return template;
  }

  const { caseSensitive = false, formats = ['#', '<>', '{}'] } = options;

  // Single-pass replacement using a combined regex pattern
  // This is more efficient than multiple passes
  const patternParts = [];

  if (formats.includes('#')) {
    patternParts.push('#(\\w+)\\b');
  }
  if (formats.includes('<>')) {
    patternParts.push('<(\\w+)>');
  }
  if (formats.includes('{}')) {
    patternParts.push('\\{(\\w+)\\}');
  }

  if (patternParts.length === 0) {
    console.warn('No valid placeholder formats specified');
    return template;
  }

  const flags = caseSensitive ? 'g' : 'gi';
  const combinedPattern = new RegExp(patternParts.join('|'), flags);

  return template.replace(combinedPattern, (match, hash, angle, brace) => {
    // Extract the key from whichever group matched
    const key = hash || angle || brace;

    // Handle case sensitivity for lookup
    const lookupKey = caseSensitive
      ? key
      : Object.keys(replacements).find(
          k => k?.toLowerCase() === key?.toLowerCase(),
        ) || key;

    const value = replacements[lookupKey];

    // Return the replacement value if found, otherwise return the original match
    return typeof value === 'string' ? value : match;
  });
};
